import { Progress, Grid, Text, Box, Stack, Popover } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useState, useRef, useEffect, forwardRef } from "react"

import { HelpHover } from "../../molecules/HelpHover/HelpHover"

export interface ProgresBoxSection {
  color?: string
  text?: string
  label?: string
  textColor?: string
  value: number
  popover?: string | React.ReactNode
}

interface ProgressBoxProps {
  sections: ProgresBoxSection[]
  label?: string | React.ReactNode
  helpHoverText: string | React.ReactNode
  helpHoverRedirectTo?: string
}

export const ProgressBox = forwardRef<HTMLDivElement, ProgressBoxProps>(
  ({ sections, helpHoverText, label }, ref) => {
    return (
      <Stack px="0" ref={ref}>
        <Grid>
          <Grid.Col span={11} p="0">
            <Stack gap={1}>
              <Progress.Root size="xxl">
                {sections.map((section, index) => {
                  const hasPopover = section.popover !== undefined

                  return (
                    <ProgressSection
                      key={section.label ?? index}
                      section={section}
                      hasPopover={hasPopover}
                    />
                  )
                })}
              </Progress.Root>
            </Stack>
          </Grid.Col>
          <Grid.Col span={1} p="0" h="100%" style={{ alignSelf: "center" }}>
            <HelpHover>{helpHoverText}</HelpHover>
          </Grid.Col>
        </Grid>
        {label && (
          <Box>
            <Text fz="xs" c="var(--mantine-color-dimmed)">
              {label}
            </Text>
          </Box>
        )}
      </Stack>
    )
  }
)

interface ProgressSectionProps {
  section: ProgresBoxSection
  hasPopover: boolean
}

const ProgressSection = ({ section, hasPopover }: ProgressSectionProps) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [isHovered, setIsHovered] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  const handleMouseEnter = () => {
    if (!hasPopover) return

    // Clear any pending close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    if (!hasPopover) return

    setIsHovered(false)

    // Close after a small delay to allow moving to the popover
    closeTimerRef.current = setTimeout(() => {
      close()
    }, 200)
  }

  const handleClick = () => {
    if (!hasPopover) return

    if (opened) {
      close()
    } else {
      open()
    }
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  return (
    <Popover
      opened={opened}
      position="top"
      withArrow
      disabled={!hasPopover}
      closeOnClickOutside
      onClose={close}
    >
      <Popover.Target>
        <Progress.Section
          p="xs"
          pos="relative"
          value={section.value}
          color={section.color ?? "var(--progress-bar-color-filled)"}
          style={{
            cursor: hasPopover ? "pointer" : "default",
            position: "relative",
          }}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {hasPopover && (
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.15)",
                opacity: isHovered ? 1 : 0,
                transition: "opacity 0.2s ease",
                pointerEvents: "none",
              }}
            />
          )}
          <Progress.Label style={{ position: "relative", zIndex: 1 }}>
            <Text
              fw={600}
              fz="sm"
              c={section.textColor ?? "var(--progress-bar-filled-text)"}
            >
              {section.text}
            </Text>
          </Progress.Label>
        </Progress.Section>
      </Popover.Target>
      {hasPopover && (
        <Popover.Dropdown
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {typeof section.popover === "string" ? (
            <Text size="sm">{section.popover}</Text>
          ) : (
            section.popover
          )}
        </Popover.Dropdown>
      )}
    </Popover>
  )
}
